import React from 'react';

import { useSelector, useUserRole } from '../../hooks/usePrerenderUser';
import { isRenderProofEntryPointEligible } from './RenderProofEntryPointEligibility';
import { useRenderProofExperience } from './useRenderProofExperience';
import { useRenderProofSeen } from './useRenderProofSeen';

export type RenderProofEntryPoint = {
  /**
   * The account is in the arm, inside the window, and has not been served the
   * proof on any surface. Whether the banner actually appears also depends on a
   * proof existing — the component decides that part.
   */
  isCandidate: boolean;
  /**
   * Re-answers the same question against the clock right now. `isCandidate` is
   * computed while rendering, and an idle dashboard can cross the end of the
   * window without re-rendering — leaving a banner on screen that is no longer
   * entitled to be clicked.
   */
  isCandidateNow: () => boolean;
  markViewed: () => void;
  dismiss: () => void;
};

export function useRenderProofEntryPoint(): RenderProofEntryPoint {
  const experience = useRenderProofExperience();
  const trackingCodeInstalledDate = useSelector(
    (state) => state.prerenderUser.trackingCodeInstalledDate,
  );
  const role = useUserRole();
  const { isKnown, viewed, dismissed, markViewed, dismiss } =
    useRenderProofSeen();

  const eligibility = {
    experience,
    role,
    trackingCodeInstalledDate,
    isSeenStateKnown: isKnown,
    viewed,
    dismissed,
  };

  const isCandidate = isRenderProofEntryPointEligible({
    ...eligibility,
    now: new Date(),
  });

  // Memoised on the inputs it reads: an effect lists this in its dependencies,
  // and a fresh closure each render would re-run that effect every render.
  const isCandidateNow = React.useCallback(
    () => isRenderProofEntryPointEligible({ ...eligibility, now: new Date() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      experience,
        role,
      trackingCodeInstalledDate,
      isKnown,
      viewed,
      dismissed,
    ],
  );

  return { isCandidate, isCandidateNow, markViewed, dismiss };
}
